import {
  CalculatorOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  IdcardOutlined,
  UserOutlined,
} from '@ant-design/icons';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import { USC_EXEMPTION_TYPE } from '@nxsys/constants/psr';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { IAepn } from '@nxsys/types/models/aepn';
import { Card, Col, Divider, Row, Space, Tag, Typography } from 'antd';
import moment from 'moment';
import React from 'react';
import { useTheme } from 'styled-components';

const { Title, Text } = Typography;

type AepnModalProps = {
  isOpen: boolean;
  toggle: () => void;
  data: IAepn | null;
};

const AEPNDetailModal: React.FC<AepnModalProps> = ({
  isOpen,
  toggle,
  data,
}: AepnModalProps) => {
  const { messages } = useTypedIntl();
  const theme = useTheme();

  if (!data) return;

  const sections = [
    {
      title: 'Employee Details',
      icon: <UserOutlined style={{ color: theme.palette.blue[500] }} />,
      color: theme.palette.blue[500],
      items: [
        {
          label: messages['common.employeeId'],
          value: data?.subcontractor?.id ?? data?.subcontractorId ?? '-',
          icon: <UserOutlined className="text-gray-600" />,
        },
        {
          label: messages['common.forename'],
          value: data?.subcontractor?.forename ?? '-',
          icon: <UserOutlined className="text-gray-600" />,
        },
        {
          label: messages['common.surname'],
          value: data?.subcontractor?.surname ?? '-',
          icon: <UserOutlined className="text-gray-600" />,
        },
        {
          label: messages['common.employerRef'],
          value: data?.subcontractor?.agencyRef,
          icon: <UserOutlined className="text-gray-600" />,
        },
        {
          label: messages['rpn.ppsNumber'],
          value: data?.employeePPSN ?? '-',
          icon: <IdcardOutlined className="text-gray-600" />,
        },
        {
          label: messages['rpn.employmentId'],
          value: data?.employmentID ?? '-',
          icon: <CreditCardOutlined className="text-gray-600" />,
        },
      ],
    },
    {
      title: 'AEPN Details',
      icon: <FileTextOutlined style={{ color: theme.palette.green[6] }} />,
      color: theme.palette.green[6],
      items: [
        {
          label: 'AEPN Issue Date',
          value: data?.aepnIssueDate
            ? moment(data?.aepnIssueDate, 'YYYY-MM-DD').format('DD/MM/YYYY')
            : '-',
          icon: <CalendarOutlined className="text-gray-600" />,
        },
        {
          label: 'AEPN Download Date',
          value: data?.aepnDownloadDateTime
            ? moment(data?.aepnDownloadDateTime, 'YYYY-MM-DD').format(
                'DD/MM/YYYY',
              )
            : '-',
          icon: <CalendarOutlined className="text-gray-600" />,
        },
        {
          label: 'AEPN Number',
          value: data?.aepnNumber ?? '-',
          icon: <FileTextOutlined className="text-gray-600" />,
        },
        {
          label: 'Enrolment Status',
          value: data?.enrolmentStatus ?? '-',
          icon: <FileTextOutlined className="text-gray-600" />,
        },
        {
          label: 'AEPN Enrolment Date',
          value: data?.aepnEnrolmentDate
            ? moment(data?.aepnEnrolmentDate, 'YYYY-MM-DD').format('DD/MM/YYYY')
            : '-',
          icon: <CalendarOutlined className="text-gray-600" />,
        },
        {
          label: 'AEPN Change Event',
          value: data?.aepnChangeEvent ? data?.aepnChangeEvent : '-',
          icon: <FileTextOutlined className="text-gray-600" />,
        },
        {
          label: 'Employee Contribution Rate',
          value: data?.eeContributionRate ? data?.eeContributionRate : '-',
          icon: <CalculatorOutlined className="text-gray-600" />,
        },
        {
          label: 'Employer Contribution Rate',
          value: data?.erContributionRate ? data?.erContributionRate : '-',
          icon: <CalculatorOutlined className="text-gray-600" />,
        },
      ],
    },
  ];

  const formatValue = (value: any) => {
    if (typeof value === 'number') {
      return value.toLocaleString('en-IE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    }
    return value ?? '-';
  };

  const getStatusTag = (value: any) => {
    if (value === USC_EXEMPTION_TYPE.EXEMPT) {
      return <Tag color={theme.palette.success.main}>EXEMPT</Tag>;
    } else if (value === USC_EXEMPTION_TYPE.NOT_EXEMPT) {
      return <Tag color={theme.palette.red[6]}>NOT EXEMPT</Tag>;
    }
    return value;
  };

  return (
    <AppModalContainer
      title={
        <Space direction="vertical" size={0} className="w-full">
          <Title level={4} className="m-0 text-gray-900">
            AEPN Details
          </Title>
          <Text type="secondary" className="text-sm text-gray-500">
            Issued {moment(data.aepnIssueDate).format('D MMMM YYYY')}
          </Text>
        </Space>
      }
      isOpen={isOpen}
      toggle={toggle}
      width={800}
    >
      <div className="py-4">
        <Row gutter={[24, 24]}>
          {sections.map((section, index) => (
            <Col key={index} span={24}>
              <Card
                size="small"
                className="rounded-lg shadow-sm border border-gray-200"
                bodyStyle={{ padding: '20px' }}
              >
                <div className="mb-4">
                  <Space align="center">
                    {section.icon}
                    <Title
                      level={5}
                      className="m-0"
                      style={{ color: section.color }}
                    >
                      {section.title}
                    </Title>
                  </Space>
                  <Divider className="my-3" />
                </div>

                <Row gutter={[16, 12]}>
                  {section?.items?.map((item, _index) => (
                    <Col key={_index} span={12}>
                      <div className="p-3 px-4 bg-gray-50 rounded-md border border-gray-200">
                        <Space align="center" className="mb-1">
                          {item.icon}
                          <Text strong className="text-xs text-gray-900">
                            {item.label}
                          </Text>
                        </Space>
                        <div className="ml-6">
                          {item.label === messages['rpn.exemption'] ? (
                            getStatusTag(item.value)
                          ) : (
                            <Text className="text-sm text-gray-900 font-medium">
                              {formatValue(item.value)}
                            </Text>
                          )}
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </AppModalContainer>
  );
};

export default AEPNDetailModal;
